/**
 * Validates job form data before saving from the admin review page.
 * Returns { valid: boolean, errors: string[] }
 *
 * 2026-05-07 — Hardware Change refactor Phase 3 added HC as a fourth
 * jobType value. Stain/sweep allowlists tightened so refinish-specific
 * checks don't fire on HC-only jobs. Photos required iff any line item
 * on the linked invoice has `requiresPhotos !== false`.
 */
export function validateJobFormReview(formData) {
  const errors = [];

  // Photos — required iff any invoice line item has `requiresPhotos !== false`.
  // Pre-Phase-2-backfill, every existing line item has `requiresPhotos === null`
  // so this rule keeps photos required everywhere they were before. After
  // backfill, Tip rows are `false`, Refinish/Maintenance are `true`, HC are
  // `false`. A mixed invoice with even one `requiresPhotos: true` row keeps
  // photos required.
  const invoiceItems = Array.isArray(formData.invoiceItems) ? formData.invoiceItems : [];
  const photosRequired =
    invoiceItems.length === 0
      ? true // safe default — no items shouldn't happen, but require photos if it does
      : invoiceItems.some((it) => it.requiresPhotos !== false);
  if (photosRequired) {
    const hasBaGroups =
      Array.isArray(formData.bagroupIds) && formData.bagroupIds.length > 0;
    if (!hasBaGroups) {
      errors.push("At least one before/after photo group is required.");
    }
  }

  // Stain required when jobType resolves to "R" or "RM" (allowlist not
  // blocklist — adding HC as a fourth value tightened this rule). M and
  // HC don't require stain.
  if (formData.jobType === "R" || formData.jobType === "RM") {
    if (!formData.stainOptionId && !formData.stainIsOther) {
      errors.push("Stain selection is required.");
    }
    if (formData.stainIsOther && !formData.customStainText?.trim()) {
      errors.push("Custom stain text is required when 'Other' is selected.");
    }
  }

  // Hardware required: same pattern, applies to all jobTypes including HC.
  if (!formData.hardwareOptionId && !formData.hardwareIsOther) {
    errors.push("Hardware brand selection is required.");
  }
  if (formData.hardwareIsOther && !formData.customHardwareText?.trim()) {
    errors.push("Custom hardware text is required when 'Other' is selected.");
  }

  // Door sweep — required when jobType resolves to R/M/RM. The door comes
  // off for refinish or maintenance work; HC is a hardware-only swap so the
  // door doesn't come off and the sweep question is moot. Field stays
  // editable on the form so a value can be recorded if for some reason it
  // was replaced.
  if (
    formData.jobType === "R" ||
    formData.jobType === "M" ||
    formData.jobType === "RM"
  ) {
    if (formData.replacedDoorSweep === null || formData.replacedDoorSweep === undefined) {
      errors.push("Door sweep selection is required.");
    }
  }

  // Dry door — same R/M/RM allowlist as door sweep (post job form omnibus
  // Phase 7, 2026-05-14). HC exempt — door doesn't come off so dry-door
  // status is moot. Mirrors stain shape: pick an option OR check Other +
  // provide custom text.
  if (
    formData.jobType === "R" ||
    formData.jobType === "M" ||
    formData.jobType === "RM"
  ) {
    if (!formData.dryDoorOptionId && !formData.dryDoorIsOther) {
      errors.push("Dry door selection is required.");
    }
    if (formData.dryDoorIsOther && !formData.customDryDoorText?.trim()) {
      errors.push("Custom dry door text is required when 'Other' is selected.");
    }
  }

  // Threshold is adjustable — required on R/M/RM. Mirrors replacedDoorSweep
  // null-check shape (post job form omnibus Phase 7, D11). Schema default
  // is `false` so this effectively only fires on legacy records or
  // explicit-null writes.
  if (
    formData.jobType === "R" ||
    formData.jobType === "M" ||
    formData.jobType === "RM"
  ) {
    if (
      formData.thresholdIsAdjustable === null ||
      formData.thresholdIsAdjustable === undefined
    ) {
      errors.push("Threshold-is-adjustable must be specified.");
    }
  }

  // Foam Wedges — always optional; empty is valid on every path. When a
  // value is provided it must be digits-only (the input already filters
  // keystrokes; this guards programmatic/paste edge cases).
  if (
    typeof formData.foamWedges === "string" &&
    formData.foamWedges.trim() !== "" &&
    !/^\d+$/.test(formData.foamWedges.trim())
  ) {
    errors.push("Foam Wedges must contain numbers only.");
  }

  // At least 1 refinisher (selected user IDs or custom names)
  const hasSelectedUsers =
    Array.isArray(formData.selectedRefinisherUserIds) &&
    formData.selectedRefinisherUserIds.length > 0;
  const hasCustomNames =
    Array.isArray(formData.customRefinisherNames) &&
    formData.customRefinisherNames.filter((n) => n.trim()).length > 0;

  if (!hasSelectedUsers && !hasCustomNames) {
    errors.push("At least one refinisher is required.");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Push 1 build 146 — stricter validation for the approval flow.
 * Includes everything in validateJobFormReview PLUS:
 *   - Referral source must be set (either a selected sourceOptionId
 *     OR a non-empty custom source string). Approval creates a ledger
 *     item; ledger with null referredBy is exactly what we want to
 *     prevent.
 *   - paymentMethod must NOT be "Call For Payment". The mobile-side
 *     "Call For Payment" option lets a refinisher submit when they
 *     couldn't take payment in the field — the office is supposed to
 *     run the card via the payment portal first. Server-side enforces
 *     this same rule (updateJobFormStatus rejects approval); this is
 *     the client-side first line of defense for clearer UX.
 */
export function validateJobFormReviewForApproval(formData) {
  const baseResult = validateJobFormReview(formData);
  const errors = [...baseResult.errors];

  const isOther = Boolean(formData.referredByIsOther);
  const hasSourceOption = Boolean(formData.referredBySourceOptionId);
  const hasCustomSource =
    typeof formData.referredBySource === "string" &&
    formData.referredBySource.trim().length > 0;
  // 2026-04-29: split into two error cases so the message is specific.
  // Previously the "Other"-with-empty-text case fell through this same
  // generic message because the UI derived isOther from non-empty source.
  // Now isOther is a real flag — the more specific error owns its case.
  if (!isOther && !hasSourceOption && !hasCustomSource) {
    errors.push("Referral source is required to approve a job form.");
  }
  if (isOther && !hasCustomSource) {
    errors.push("Custom referral source is required when 'Other' is selected.");
  }

  // Self-heal: only block when the card genuinely hasn't been run. If
  // the linked invoice is already paid, the gate's reason (no payment
  // collected) is satisfied — the server-side updateJobFormStatus
  // self-heals the stale field on approve, so don't reject in the UI.
  if (formData.paymentMethod === "Call For Payment" && !formData.invoicePaid) {
    errors.push(
      "Cannot approve while payment method is 'Call For Payment'. Take payment via the payment portal first; the form will unblock automatically."
    );
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
